import { Button, Tag } from "antd"
import { useEffect, useMemo, useState } from "react"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import apiClient from "../../../Utils/apiClient"
import PanelLayout from "../../../Layout/PanelLayout"
import TableBox from "../../../components/TableBox/TableBox"
import HostnameLink from "../../../components/HostnameLink/HostnameLink"
import DeviceModelImage from "../../../components/DeviceModelImage/DeviceModelImage"
import EmptyState from "../../../components/EmptyState/EmptyState"
import AutoCompleteBox from "../../../components/AutoCompleteBox/AutoCompleteBox"
import { getStoredPageSize } from "../../../Utils/TableUtils/TableUtils"
import { renderDate } from "../../../Utils/utils"
import { resolveDeviceModelImage } from "../../../Utils/deviceModelImage"
import "../../Operations/Operations.scss"

dayjs.extend(relativeTime)

const shortUser = (value) => {
  const text = String(value || "").trim()
  if (!text) return "Unassigned"
  const parts = text.split(/\\|\//)
  return parts[parts.length - 1] || text
}

const STATUS_META = {
  ok: { label: "Ok", color: "success" },
  stale: { label: "Stale", color: "warning" },
  "wrong-server": { label: "Wrong server", color: "error" },
  "not-configured": { label: "Not configured", color: "default" },
  unknown: { label: "Unknown", color: "processing" },
}

const WsusStatus = () => {
  const page = "WSUS Status"
  const [filters, setFilters] = useState({ hostname: "", user: "" })
  const [statusChip, setStatusChip] = useState("")
  const [paginationData, setPaginationData] = useState({
    page: 1,
    pageSize: getStoredPageSize(page) || 10,
    total: 0,
  })
  const [loading, setLoading] = useState(true)
  const [devices, setDevices] = useState([])
  const [summary, setSummary] = useState({
    all: 0,
    ok: 0,
    stale: 0,
    wrongServer: 0,
    notConfigured: 0,
    unknown: 0,
  })

  const fetchData = async (
    customPage = paginationData.page,
    customPageSize = paginationData.pageSize,
    customFilters = filters,
    customStatus = statusChip
  ) => {
    setLoading(true)
    try {
      const params = {
        page: customPage,
        pageSize: customPageSize,
        hostname: customFilters.hostname || undefined,
        user: customFilters.user || undefined,
        status: customStatus || undefined,
      }
      const res = await apiClient("devices/wsus-status", "GET", null, { params })
      if (res?.data) {
        setDevices(res.data)
        setPaginationData(res.pageData || { page: customPage, pageSize: customPageSize, total: 0 })
        if (res.summary) setSummary(res.summary)
      }
    } catch (err) {
      console.log("Error fetching WSUS status", err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData(1, paginationData.pageSize)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleTableChange = (pagination) => {
    const { current, pageSize } = pagination
    setPaginationData((prev) => ({ ...prev, page: current, pageSize }))
    fetchData(current, pageSize)
  }

  const handleClearFilter = () => {
    const empty = { hostname: "", user: "" }
    setFilters(empty)
    setStatusChip("")
    fetchData(1, paginationData.pageSize, empty, "")
  }

  const statusKpis = useMemo(
    () => [
      { key: "", label: "All", value: summary.all, tone: "#2563eb", soft: "rgba(37, 99, 235, 0.12)" },
      { key: "ok", label: "Ok", value: summary.ok, tone: "#059669", soft: "rgba(34, 197, 94, 0.14)" },
      {
        key: "stale",
        label: "Stale",
        value: summary.stale,
        tone: "#d97706",
        soft: "rgba(245, 158, 11, 0.16)",
      },
      {
        key: "wrong-server",
        label: "Wrong server",
        value: summary.wrongServer,
        tone: "#b91c1c",
        soft: "rgba(185, 28, 28, 0.14)",
      },
      {
        key: "not-configured",
        label: "Not configured",
        value: summary.notConfigured,
        tone: "#64748b",
        soft: "rgba(100, 116, 139, 0.14)",
      },
      {
        key: "unknown",
        label: "Unknown",
        value: summary.unknown,
        tone: "#7c3aed",
        soft: "rgba(124, 58, 237, 0.12)",
      },
    ],
    [summary]
  )

  const columns = [
    {
      title: "Device",
      dataIndex: "hostname",
      key: "hostname",
      width: 280,
      render: (text, record) => {
        const model = resolveDeviceModelImage(record)
        return (
          <div className="inv-device">
            <DeviceModelImage device={record} size="sm" />
            <div className="inv-device__copy">
              <div className="inv-device__title">
                <span
                  className={`inv-device__pulse inv-device__pulse--${String(
                    record.deviceStatus || "offline"
                  )
                    .toLowerCase()
                    .replace(/\s+/g, "-")}`}
                  aria-hidden
                />
                <HostnameLink record={record}>{text}</HostnameLink>
              </div>
              <div className="inv-device__meta" title={record.user || ""}>
                <span>{shortUser(record.user)}</span>
                <span className="inv-device__dot" />
                <span title={model.displayModel}>{model.label}</span>
              </div>
            </div>
          </div>
        )
      },
    },
    {
      title: "WSUS status",
      dataIndex: "wsusStatus",
      key: "wsusStatus",
      width: 140,
      render: (status) => {
        const meta = STATUS_META[status] || STATUS_META.unknown
        return <Tag color={meta.color}>{meta.label}</Tag>
      },
    },
    {
      title: "WSUS URL",
      key: "serverUrl",
      ellipsis: true,
      render: (_, record) => {
        const url = record.wsus?.serverUrl
        if (!url) return <span className="ops-muted">—</span>
        return <span title={url}>{url}</span>
      },
    },
    {
      title: "Last detect",
      key: "lastDetectAt",
      width: 170,
      render: (_, record) => {
        const at = record.wsus?.lastDetectAt
        if (!at) return <span className="ops-muted">Never</span>
        return (
          <div className="reboot-when">
            <span>{renderDate(at)}</span>
            <span className="ops-muted">
              {record.detectAgeHours != null
                ? `${record.detectAgeHours}h ago`
                : dayjs(at).fromNow()}
            </span>
          </div>
        )
      },
    },
    {
      title: "Last download / install",
      key: "lastDlInstall",
      width: 180,
      render: (_, record) => {
        const dl = record.wsus?.lastDownloadAt
        const inst = record.wsus?.lastInstallAt
        if (!dl && !inst) return <span className="ops-muted">—</span>
        return (
          <div className="reboot-when">
            <span className="ops-muted">
              DL {dl ? dayjs(dl).fromNow() : "—"}
            </span>
            <span className="ops-muted">
              Inst {inst ? dayjs(inst).fromNow() : "—"}
            </span>
          </div>
        )
      },
    },
    {
      title: "Agent",
      dataIndex: "agentversion",
      key: "agentversion",
      width: 110,
      render: (text) => text || <span className="ops-muted">—</span>,
    },
  ]

  return (
    <PanelLayout
      title="WSUS status"
      subtitle="Client WSUS config and last successful detect — agent-reported heartbeat (not a live server probe)."
    >
      <div className="ops-kpis">
        {statusKpis.map((item) => (
          <button
            type="button"
            key={item.key || "all"}
            className={`ops-kpi${statusChip === item.key ? " is-active" : ""}`}
            style={{ "--act-tone": item.tone, "--act-soft": item.soft }}
            onClick={() => {
              setStatusChip(item.key)
              setPaginationData((prev) => ({ ...prev, page: 1 }))
              fetchData(1, paginationData.pageSize, filters, item.key)
            }}
          >
            <div>
              <span className="ops-kpi-label">{item.label}</span>
              <strong>{item.value}</strong>
            </div>
          </button>
        ))}
      </div>

      <div className="ops-toolbar">
        <span className="ops-toolbar__count">
          {paginationData.total} device{paginationData.total === 1 ? "" : "s"}
          {statusChip ? ` · ${STATUS_META[statusChip]?.label || statusChip}` : ""}
        </span>
        <Button
          size="small"
          onClick={() => fetchData(paginationData.page, paginationData.pageSize)}
        >
          Refresh
        </Button>
      </div>

      {devices.length || loading ? (
        <TableBox
          page={page}
          title={null}
          columns={columns}
          data={devices}
          loading={loading}
          className="dashboard-inventory-table"
          rowKey="_id"
          pagination={{
            current: paginationData.page,
            pageSize: paginationData.pageSize,
            total: paginationData.total,
            showSizeChanger: true,
          }}
          onChange={handleTableChange}
          enableFilter
          filtercontent={
            <div className="p-3">
              <AutoCompleteBox
                label="Hostname"
                name="hostname"
                className="mb-3 w-100"
                endPoint="devices/identifiers"
                placeholder="Search hostname"
                value={filters.hostname}
                onChange={(value) =>
                  setFilters((prev) => ({ ...prev, hostname: value }))
                }
                onSelect={(value) => {
                  const next = { ...filters, hostname: value }
                  setFilters(next)
                  setPaginationData((prev) => ({ ...prev, page: 1 }))
                  fetchData(1, paginationData.pageSize, next, statusChip)
                }}
              />
              <AutoCompleteBox
                label="User"
                name="user"
                className="mb-3 w-100"
                endPoint="users"
                placeholder="Search user"
                value={filters.user}
                onChange={(value) =>
                  setFilters((prev) => ({ ...prev, user: value }))
                }
                onSelect={(value) => {
                  const next = { ...filters, user: value }
                  setFilters(next)
                  setPaginationData((prev) => ({ ...prev, page: 1 }))
                  fetchData(1, paginationData.pageSize, next, statusChip)
                }}
              />
            </div>
          }
          onFilter={() => {
            setPaginationData((prev) => ({ ...prev, page: 1 }))
            fetchData(1, paginationData.pageSize, filters, statusChip)
          }}
          onClear={handleClearFilter}
          filters={{ ...filters, status: statusChip }}
        />
      ) : (
        <EmptyState
          title="No devices"
          description="No Windows clients match this WSUS status filter."
        />
      )}
    </PanelLayout>
  )
}

export default WsusStatus
